import { _getLogger } from '@doxyme/analytics';
import {
  BasicObservable,
  Specs,
  attachHandler,
} from '@doxyme/extensions-common';
import type Instance from '../../../instance';
import {
  logErrorAndThrowBind,
  type LogErrorAndThrowBound,
} from '../../../utils';
import { IframeRuntime } from '../../../runtimes/iframeRuntime';
import AppletCapability from '../applet';

const { PageApplet } = Specs;

type Constraints = Specs.PageApplet.Constraints;
type QueryParamData = Specs.PageApplet.QueryParamData;
type OpenPayload = Specs.PageApplet.OpenPayload;
type ClosePayload = Specs.PageApplet.ClosePayload;
type Applet = Specs.PageApplet.Applet;

const logger = _getLogger('pageApplet', {
  component: 'extensions',
  lib: 'bridge',
});
const logErrorAndThrow: LogErrorAndThrowBound = logErrorAndThrowBind(logger);

export type OpenHandler = {
  (manifestId: string, name?: string, data?: QueryParamData): Promise<void>;
};
export type CloseHandler = {
  (): Promise<void>;
};

/**
 * Page Applet exposes the active frame registered to any extension using this capability and attached.
 * As well as methods to assign open/close handlers and attach method to give a control to the host app.
 *
 * Intended use is inside page applet dedicated router page.
 */
export default class PageAppletCapability extends AppletCapability<Constraints> {
  static id = PageApplet.id;

  protected override readonly hasDynamicHeight = true;

  private readonly activeFrame: BasicObservable<Applet | undefined>;

  private readonly openHandlers: OpenHandler[] = [];

  private readonly closeHandlers: CloseHandler[] = [];

  constructor() {
    super(PageApplet);

    this.activeFrame = new BasicObservable<Applet | undefined>(
      this._frames.value[0],
    );
    this._frames.onChange((frames) => (this.activeFrame.value = frames[0]));

    this.handlers.push(
      [PageApplet.Actions.Open, this.open.bind(this)],
      [PageApplet.Actions.Close, this.closeAndDetach.bind(this)],
    );
  }

  /**
   * Override to prevent auto attaching of page frames
   */
  override addInstance(instance: Instance): void {
    super.addInstanceBase(instance);
  }

  /**
   * Get the active frame
   * @description This is an observable that will update whenever the active frame is updated. See {@link BasicObservable} for more information.
   * @returns The active frame if available, as an observable.
   * @example
   * ```ts
   * const activeFrame = configApplet.getActiveFrame();
   * activeFrame.onChange((frame) => {
   *  console.log('Active frame changed', frame);
   * });
   * ```
   */
  getActiveFrame(): BasicObservable<Applet | undefined> {
    return this.activeFrame;
  }

  /**
   * Assign onOpen handler, will be called on "open" action.
   */
  onOpen(handler: OpenHandler): () => void {
    return attachHandler<OpenHandler>(this.openHandlers, handler);
  }

  /**
   * Assign onClose handler, will be called on "close" action.
   */
  onClose(handler: CloseHandler): () => void {
    return attachHandler<CloseHandler>(this.closeHandlers, handler);
  }

  /**
   * Attach page frame
   * This method is exposed to the host app and responsible for attaching a page frame.
   * Attaches only one frame from the capabilities configuration if multiple defined.
   */
  attachApplet(
    manifestId: string,
    configName?: string,
    queryParamData?: string,
  ): void {
    const data = this.parseQueryParamData(queryParamData);

    const instance = this.getInstanceByManifestId(manifestId);
    this.attachOne(configName, instance);

    if (data !== undefined) {
      const runtime = this.getRuntimeOrThrow(configName, instance);
      runtime.appendQueryParams(this.encodeQueryParamData(data));
    }
  }

  private getRuntimeOrThrow(
    configName: string | undefined,
    instance: Instance,
  ): IframeRuntime {
    const configs = this.getConfigs(configName, instance);
    const config = configs[0];
    const runtime = config
      ? this.runtimes[IframeRuntime.runtimeId(instance, config)]
      : undefined;

    if (!runtime) {
      logErrorAndThrow('[getRuntime] Runtime not found', {
        ...this.getLoggerProps(instance),
        configName,
      });
    }

    return runtime;
  }

  private encodeQueryParamData(data: QueryParamData): URLSearchParams {
    const params = new URLSearchParams();
    params.set('queryParamData', JSON.stringify(data));
    return params;
  }

  private parseQueryParamData(data?: string): QueryParamData {
    if (data === undefined) {
      return data;
    }

    try {
      return JSON.parse(data);
    } catch (error) {
      logErrorAndThrow('[parseQueryParamData] Error parsing query param data', {
        error,
        data,
      });
    }
  }

  /**
   * Unlike close, id does not call onClose handlers, just detaches a frame.
   */
  async detachApplet(manifestId: string, configName?: string): Promise<void> {
    const instance = this.getInstanceByManifestId(manifestId);
    this.detach(configName, instance);
  }

  /**
   * Analog of close, but coming from the host app (e.g. back button click)
   * Note: Logic could be changed to just close active frame (as it is done for config applet)
   */
  async closeApplet(manifestId: string, configName?: string): Promise<void> {
    const instance = this.getInstanceByManifestId(manifestId);
    return this.closeAndDetach(configName, instance);
  }

  /**
   * "Open" action handler, calls all assigned openHandlers
   */
  private async open(
    payload: OpenPayload | undefined,
    instance: Instance<Constraints>,
  ): Promise<void> {
    for await (const handler of this.openHandlers) {
      await handler(
        instance.manifest.id,
        payload?.configName,
        payload?.queryParamData,
      );
    }
  }

  /**
   * Just calls closeHandlers
   */
  async close(): Promise<void> {
    for await (const handler of this.closeHandlers) {
      await handler();
    }
  }

  /**
   * "Close" action handler, calls all assigned closeHandlers
   */
  private async closeAndDetach(
    payload: ClosePayload | undefined,
    instance: Instance<Constraints>,
  ): Promise<void> {
    await this.close();

    this.detach(payload, instance);
  }
}
